/** @jsxImportSource solid-js */

import { Component, Show, createSignal, createEffect, onMount } from 'solid-js';
import './CommentContainer.css';
import { IComment, IUser, useCommentApi } from '@tc-comments/providers';
import { defineSolidConfirmPopup, defineSolidSpinner } from '@tcweb/connect-ui';
import { translate } from '@fulcrum/lib';
import Comments from '../Comments/Comments';

export interface ICommentContainerProps {
  readonly objectId: string | undefined;
  readonly objectType: string;
  readonly addedComment?: IComment;
  readonly projectId: string;
  commentsCount(count: number): void;
  getCommentDetail(commentDetail: IComment): void;
  readonly isPreviewOn: boolean;
  isCommentActionAllowed(commentAction: string): boolean;
  readonly policyToolTip: unknown;
  readonly user: IUser;
  readonly userRole?: string;
  readonly project: {
    readonly id: string;
    readonly name: string;
    readonly rootId: string;
  };
  readonly projectUser: readonly IUser[];
  // Confirm popup event handlers
  onClose?(): void;
  onAction?(): void;
  readonly contextId: string | undefined;
  /**
   * Called after a comment is successfully deleted, with the ids of any
   * VIEW-type attachments that were linked to it, so the host can clean up
   * the associated issue views.
   */
  onCommentDeleted?(
    commentId: string,
    viewAttachmentIds: readonly string[]
  ): void;
}

const CommentContainer: Component<ICommentContainerProps> = (props) => {
  const commentApiService = useCommentApi();
  const [comments, setComments] = createSignal<IComment[]>([]);
  const [isLoading, setIsLoading] = createSignal(false);
  const [isUpdating, setIsUpdating] = createSignal(false);
  const [isDeleting, setIsDeleting] = createSignal(false);
  const [deleteCommentIndex, setDeleteCommentIndex] = createSignal(-1);

  onMount(() => {
    // Initialize the confirm popup web component
    defineSolidConfirmPopup();
    defineSolidSpinner();
    loadComments();
  });

  // Watch for added comments - refetch all comments to get the latest data
  createEffect(() => {
    const newAddedComment = props.addedComment;
    if (newAddedComment?.id) {
      // Refetch all comments to ensure we have the most up-to-date data
      // including proper hasAttachment flags and any other server-side updates
      loadComments();
    }
  });

  const loadComments = async (): Promise<void> => {
    const projectId = props.projectId;
    const objectId = props.objectId;
    setComments([]);
    setIsLoading(true);

    try {
      const response = await commentApiService.getComments({
        objectId: objectId,
        projectId,
        contextId: props.contextId,
        objectType: props.objectType,
        page: 1,
        limit: 50,
      });

      setComments([...response.comments]);
      props.commentsCount(response.comments.length);
    } catch (error) {
      console.error('Failed to load comments:', error);
      setComments([]);
      props.commentsCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteComment = async (): Promise<boolean> => {
    const index = deleteCommentIndex();
    const commentList = comments();

    if (index >= 0 && index < commentList.length) {
      const commentId = commentList[index].id;
      setIsDeleting(true);

      // Look up linked views before deleting so we can ask the host to clean
      // them up; failure here should not block the comment deletion itself.
      let viewAttachmentIds: readonly string[] = [];
      if (commentList[index].hasAttachment) {
        try {
          const attachments =
            await commentApiService.getCommentAttachments(commentId);
          viewAttachmentIds = attachments
            .filter(
              (attachment) =>
                attachment.type === 'VIEW' || attachment.type === 'VIEW2D'
            )
            .map((attachment) => attachment.id);
        } catch (error) {
          console.error(
            'Failed to fetch comment attachments before delete:',
            error
          );
        }
      }

      try {
        await commentApiService.deleteComment({
          commentId,
        });

        // Remove comment from local state after successful deletion
        const newComments = [...commentList];
        newComments.splice(index, 1);
        props.commentsCount(newComments.length);
        setComments(newComments);
        setDeleteCommentIndex(-1);
        props.onCommentDeleted?.(commentId, viewAttachmentIds);
        return true;
      } catch (error) {
        console.error('Failed to delete comment:', error);
        return false;
      } finally {
        setIsDeleting(false);
      }
    }
    return false;
  };

  const updateComment = async (
    commentId: string,
    description: string
  ): Promise<boolean> => {
    setIsUpdating(true);

    try {
      const response = await commentApiService.updateComment({
        commentId,
        comment: description,
      });

      // Update the comment in local state
      const commentList = comments();
      const index = commentList.findIndex((c) => c.id === commentId);
      if (index > -1) {
        const updatedComments = [...commentList];
        updatedComments[index] = response.comment;
        setComments(updatedComments);
      }

      return true;
    } catch (error) {
      console.error('Failed to update comment:', error);
      return false;
    } finally {
      setIsUpdating(false);
    }
  };

  const updateSpecificComment = async (commentId: string): Promise<void> => {
    try {
      const response = await commentApiService.getComment(commentId);

      // Update the specific comment in local state
      const commentList = comments();
      const index = commentList.findIndex((c) => c.id === commentId);
      if (index > -1) {
        const updatedComments = [...commentList];
        updatedComments[index] = response.comment;
        setComments(updatedComments);
      }
    } catch (error) {
      console.error('Failed to refresh comment:', error);
      // Fallback to updating modified date only
      const commentList = comments();
      const index = commentList.findIndex((c) => c.id === commentId);
      if (index > -1) {
        const updatedComments = [...commentList];
        updatedComments[index] = {
          ...updatedComments[index],
        };
        setComments(updatedComments);
      }
    }
  };

  const onDeleteComment = (index: number): void => {
    setDeleteCommentIndex(index);
  };

  const onUpdateComment = (
    index: number,
    description: string
  ): Promise<boolean> => {
    const commentList = comments();
    if (index >= 0 && index < commentList.length) {
      return updateComment(commentList[index].id, description);
    }
    return Promise.resolve(false);
  };

  const handleCloseConfirm = (): void => {
    setDeleteCommentIndex(-1);
    // Call the onClose prop if provided
    if (props.onClose) {
      props.onClose();
    }
  };

  const handleConfirmAction = async (): Promise<void> => {
    const success = await deleteComment();
    if (success) {
      // Call the onAction prop if provided
      if (props.onAction) {
        props.onAction();
      }
    }
  };

  return (
    <div class="topic-comment-container">
      <div class="sub-section">
        {/* Show comments if available, regardless of loading state */}
        <Show when={comments().length > 0}>
          <Comments
            comments={comments()}
            onDeleteComment={onDeleteComment}
            onUpdateComment={onUpdateComment}
            updateSpecificComment={updateSpecificComment}
            currentUser={props.user}
            isDeleting={isDeleting()}
            isUpdating={isUpdating()}
            projectId={props.projectId}
            objectId={props.objectId}
            getCommentDetail={props.getCommentDetail}
            isPreviewOn={props.isPreviewOn}
            projectUser={props.projectUser}
            isCommentActionAllowed={props.isCommentActionAllowed}
            policyToolTip={props.policyToolTip}
          />
        </Show>

        {/* Show empty state only when not loading and no comments */}
        <Show when={!isLoading() && comments().length === 0}>
          <div class="empty-state-height">
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore */}
            <tc-empty
              icon="icon-font tc-icon-comment"
              message={translate('Todos.NoCommentsAvailable')}
              is-small-container="true"
              class="tc-empty-icon-large tc-empty-transparent"
            />
          </div>
        </Show>

        {/* Show spinner only when loading and no comments available */}
        <Show when={isLoading() && comments().length === 0}>
          <div class="text-center py-4">
            {/* @ts-expect-error - tc-spinner is a web component defined at runtime */}
            <tc-spinner show={true} />
          </div>
        </Show>
      </div>

      {/* Confirm Delete Dialog */}
      <tc-confirm-popup
        show={deleteCommentIndex() >= 0}
        title={translate('TC.Comments.Delete.ConfirmDialog.Title')}
        description={translate('deleteCommentMessage')}
        waiting={isDeleting()}
        primary-btn-label={
          isDeleting()
            ? translate('TC.Common.Deleting.Dotless') + '...'
            : translate('TC.Common.Delete')
        }
        primary-btn-class="warning"
        secondary-btn-label={translate('TC.Common.Cancel')}
        secondary-btn-class="link-secondary"
        ref={(el: HTMLElement | null) => {
          if (el) {
            // Remove any existing listeners to prevent duplicates
            el.removeEventListener('confirm-popup-action', handleConfirmAction);
            el.removeEventListener('confirm-popup-close', handleCloseConfirm);
            // Add new listeners
            el.addEventListener('confirm-popup-action', handleConfirmAction);
            el.addEventListener('confirm-popup-close', handleCloseConfirm);
          }
        }}
      />
    </div>
  );
};

export default CommentContainer;
